import { Box } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { getRequest } from 'utils/api'
import {
  GOAL_STEP_CONTENT_URL,
  GOAL_STEP_QUIZ_TYPES,
  GOAL_STEP_QUIZ_URL,
  JOURNEY_STEPS_SECTIONS
} from 'services/constants'
import StepContent from './StepContent'
import StepQuiz from './StepQuiz'
import StepPathObjectHeader from './StepPathObjectHeader'
import StepPathNavStepper from './StepPathNavStepper'
import { checkSaved } from 'utils/contentEditorHelper'
import { scrollToSection } from 'utils/contentScrollHelper'
import OverlayLoading from 'components/atoms/OverlayLoading'
import { useLocation } from 'react-router-dom'
import {
  TRACKING_SCREEN_MODELS,
  logScreen,
  trackingSubScreenKey,
  updateViewTime
} from 'services/trackingScreenActions'
import AudioPlayer from 'components/atoms/AudioPlayer'
import { trackPathAction } from 'services/amplitudeTracking'
import StepFeedbackPopup from './StepFeedbackPopup'

// This componenet manage steps pagination
const StepContentsCard = ({
  organizedSteps,
  l2StepData,
  firstL2Step,
  handleNextStep2,
  handlePreviousStep2,
  contentStartIndex,
  editorMode = false,
  pathOpen,
  isRolePlayStep,
  setIsRolePlayStep = () => {}
}) => {
  const [l1StepName, setL1StepName] = useState('')
  const [l3Steps, setL3Steps] = useState([])
  const [activeStep, setActiveStep] = useState(0)
  const [showStepper, setShowStepper] = useState(true)
  const [loading, setLoading] = useState(false)
  const [stepFeedbackOpen, setStepFeedbackOpen] = useState(false)
  const [disableNextForRoleplay, setDisableNextForRoleplay] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      const stepContents = [...l2StepData.childL3Steps]

      await Promise.all(
        stepContents.map(async step => {
          let requesUrl = ''
          const params = { goalStepId: step.id }
          if (step.type === 'content') {
            requesUrl = GOAL_STEP_CONTENT_URL
            if (!editorMode) {
              params.web = true
            }
          } else {
            requesUrl = GOAL_STEP_QUIZ_URL
          }
          if (editorMode) {
            params.editorMode = true
          }
          const res = await getRequest(requesUrl, params)
          if (res.status === 200) {
            step.contentData = res.data.data
          } else {
            step.contentData = false
          }
        })
      )

      setL3Steps(stepContents)
      if (contentStartIndex === -1) {
        setActiveStep(stepContents.length - 1)
      } else {
        setActiveStep(contentStartIndex)
      }
      scrollToSection(JOURNEY_STEPS_SECTIONS.CONTENT)
      setLoading(false)
    }
    if (l2StepData.childL3Steps) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentStartIndex, editorMode, l2StepData])

  // Screen monitor functions
  const location = useLocation()
  const screenLogRef = useRef({})
  const logSubScreen = async () => {
    const relatedModelId =
      l3Steps[activeStep]?.id ?? l3Steps[activeStep]?.stepId
    if (relatedModelId) {
      await logScreen(
        screenLogRef,
        trackingSubScreenKey,
        location,
        TRACKING_SCREEN_MODELS.GOAL_STEPS,
        relatedModelId
      )
    }
  }
  useEffect(() => {
    const updateSubScreenViewTime = async () => {
      await updateViewTime(screenLogRef, trackingSubScreenKey)
    }
    window.addEventListener('focus', logSubScreen)
    window.addEventListener('blur', updateSubScreenViewTime)
    return () => {
      window.removeEventListener('focus', logSubScreen)
      window.removeEventListener('blur', updateSubScreenViewTime)
      updateViewTime()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (organizedSteps.length && l2StepData.number) {
      const sortedSteps = [...organizedSteps].sort(
        (a, b) => b.number - a.number
      )
      const currentL1Step = sortedSteps.find(
        l1Step => l1Step.number < l2StepData.number
      )
      setL1StepName(currentL1Step.name)
    }
  }, [organizedSteps, l2StepData])

  useEffect(() => {
    if (
      l3Steps[activeStep]?.type === 'quiz' &&
      !editorMode &&
      (l3Steps[activeStep].contentData?.type === GOAL_STEP_QUIZ_TYPES.CHECK ||
        (l3Steps[activeStep].contentData?.type === GOAL_STEP_QUIZ_TYPES.TEXT &&
          !l3Steps[activeStep].contentData.userQuizResults?.length))
    ) {
      setShowStepper(false)
    } else setShowStepper(true)

    if (l3Steps[activeStep]?.type === 'quiz') {
      setIsRolePlayStep(
        l3Steps[activeStep].contentData?.type === GOAL_STEP_QUIZ_TYPES.ROLE_PLAY
      )
    } else {
      setIsRolePlayStep(false)
      setDisableNextForRoleplay(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [l3Steps, activeStep])

  const contentActionTracking = (eventName, additionalProperties) => {
    trackPathAction(l2StepData.goalId, eventName, {
      milestone: l1StepName,
      stepName: l2StepData.name,
      stepTitle: l3Steps[activeStep].contentData?.title,
      ...additionalProperties
    })
  }

  useEffect(() => {
    if (l3Steps[activeStep]) {
      logSubScreen()
      contentActionTracking('Read Content')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [l3Steps[activeStep]])

  const handleNext = () => {
    if (editorMode && !checkSaved()) {
      return
    }
    if (activeStep < l3Steps.length - 1) {
      setActiveStep(activeStep + 1)
    } else if (activeStep === l3Steps.length - 1 && l2StepData.isLastL2Step) {
      // If last l2 in milestone, check if updating user current step
      const updatedUserCurrentStepId = handleNextStep2(false, true)
      if (updatedUserCurrentStepId) {
        // If updated user current step, show feedback popup
        setStepFeedbackOpen(true)
      } else {
        // If not updated user current step, move to next step
        handleNextStep2(true, false)
      }
    } else {
      handleNextStep2()
    }
    scrollToSection(JOURNEY_STEPS_SECTIONS.CONTENT)
  }
  const handlePrevious = () => {
    if (editorMode && !checkSaved()) {
      return
    }
    if (activeStep === 0) {
      handlePreviousStep2()
    } else {
      setActiveStep(activeStep - 1)
    }
    scrollToSection(JOURNEY_STEPS_SECTIONS.CONTENT)
  }

  const contentEditorCallback = updated => {
    const updatedArray = [...l3Steps]
    updatedArray[activeStep].contentData = updated
    setL3Steps(updatedArray)
  }

  const handleStepFeedbackClose = () => {
    setStepFeedbackOpen(false)
    handleNextStep2(true, false)
  }

  const NavBtns = ({
    size = 48,
    iconSize = 24,
    conatinerSx = {},
    ...otherProps
  }) =>
    showStepper && (
      <StepPathNavStepper
        length={l3Steps.length}
        activeStep={activeStep}
        firstL2Step={firstL2Step}
        size={size}
        iconSize={iconSize}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        conatinerSx={conatinerSx}
        disableNextForRoleplay={disableNextForRoleplay}
        {...otherProps}
      />
    )

  return (
    <>
      {!isRolePlayStep && (
        <StepPathObjectHeader
          l1StepName={l1StepName}
          stepData={l3Steps[activeStep]?.contentData}
          stepType={l3Steps[activeStep]?.type}
          navBtns={
            <NavBtns
              size={32}
              iconSize={20}
              nextIconTestId={'NavArrowForwardIcon'}
              backIconTestId={'NavArrowBackIcon'}
            />
          }
          scrollToSection={scrollToSection}
        />
      )}
      {l3Steps.length > 0 && (
        <>
          <Box sx={{ marginBottom: 8 }} id={JOURNEY_STEPS_SECTIONS.CONTENT}>
            {l3Steps[activeStep].type === 'content' ? (
              <StepContent
                stepData={l3Steps[activeStep].contentData}
                editorMode={editorMode}
                contentEditorCallback={contentEditorCallback}
                contentActionTracking={contentActionTracking}
              />
            ) : (
              l3Steps[activeStep].type === 'quiz' && (
                <StepQuiz
                  goalStepId={l3Steps[activeStep].id}
                  editorMode={editorMode}
                  scrollToTop={() =>
                    scrollToSection(JOURNEY_STEPS_SECTIONS.CONTENT)
                  }
                  setShowStepper={setShowStepper}
                  pathOpen={pathOpen}
                  contentActionTracking={contentActionTracking}
                  setDisableNextForRoleplay={setDisableNextForRoleplay}
                />
              )
            )}
          </Box>

          <NavBtns conatinerSx={{ marginBottom: 30 }} />

          {l3Steps[activeStep]?.contentData?.audio_url && (
            <AudioPlayer
              url={l3Steps[activeStep]?.contentData?.audio_url}
              contentActionTracking={contentActionTracking}
            />
          )}
        </>
      )}
      <OverlayLoading open={loading} />
      <StepFeedbackPopup
        open={stepFeedbackOpen}
        onClose={handleStepFeedbackClose}
        organizedSteps={organizedSteps}
        l2StepData={l2StepData}
      />
    </>
  )
}

export default StepContentsCard
